import { FC, useCallback, useId } from "react";

import { Box, HStack, Text } from "@/components/ui/base";

interface RangeSliderProps {
  disabled?: boolean;
  max: number;
  min: number;
  onMaxChange: (value: number) => void;
  onMinChange: (value: number) => void;
  step?: number;
  trackColor?: string;
  valueMax: number;
  valueMin: number;
}

const THUMB = 16;

const RangeSlider: FC<RangeSliderProps> = ({
  disabled = false,
  max,
  min,
  onMaxChange,
  onMinChange,
  step = 1,
  trackColor = "rgb(var(--color-primary-500))",
  valueMax,
  valueMin,
}) => {
  const id = useId();
  const cls = `rs-${id.replace(/:/g, "")}`;

  const pct = (v: number) => ((v - min) / (max - min)) * 100;

  const handleMinInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const v = Number(e.target.value);
      if (v < valueMax) onMinChange(v);
    },
    [onMinChange, valueMax]
  );

  const handleMaxInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const v = Number(e.target.value);
      if (v > valueMin) onMaxChange(v);
    },
    [onMaxChange, valueMin]
  );

  return (
    <Box className="w-full">
      <div style={{ height: 24, position: "relative", width: "100%" }}>
        {/* Background track */}
        <div
          style={{
            backgroundColor: "rgb(var(--color-outline-200))",
            borderRadius: 4,
            height: 4,
            left: 0,
            position: "absolute",
            right: 0,
            top: "50%",
            transform: "translateY(-50%)",
          }}
        />
        {/* Filled track */}
        <div
          style={{
            backgroundColor: trackColor,
            borderRadius: 4,
            height: 4,
            left: `${pct(valueMin)}%`,
            position: "absolute",
            top: "50%",
            transform: "translateY(-50%)",
            width: `${pct(valueMax) - pct(valueMin)}%`,
          }}
        />
        {/* Min range input */}
        <input
          className={cls}
          disabled={disabled}
          max={max}
          min={min}
          onChange={handleMinInput}
          step={step}
          style={{
            WebkitAppearance: "none",
            appearance: "none",
            background: "transparent",
            height: 0,
            left: 0,
            margin: 0,
            padding: 0,
            pointerEvents: "none",
            position: "absolute",
            top: "50%",
            transform: "translateY(-50%)",
            width: "100%",
            zIndex: valueMin > max - 10 ? 5 : 3,
          }}
          type="range"
          value={valueMin}
        />
        {/* Max range input */}
        <input
          className={cls}
          disabled={disabled}
          max={max}
          min={min}
          onChange={handleMaxInput}
          step={step}
          style={{
            WebkitAppearance: "none",
            appearance: "none",
            background: "transparent",
            height: 0,
            left: 0,
            margin: 0,
            padding: 0,
            pointerEvents: "none",
            position: "absolute",
            top: "50%",
            transform: "translateY(-50%)",
            width: "100%",
            zIndex: 4,
          }}
          type="range"
          value={valueMax}
        />
      </div>
      <HStack className="justify-between mt-1">
        <Text className="text-[10px] text-typography-400">{min}</Text>
        <Text className="text-[10px] text-typography-400">{max}</Text>
      </HStack>
      <style
        dangerouslySetInnerHTML={{
          __html: `
            input[type="range"].${cls} {
              -webkit-appearance: none;
              appearance: none;
              outline: none;
            }
            input[type="range"].${cls}::-webkit-slider-thumb {
              -webkit-appearance: none;
              appearance: none;
              pointer-events: all;
              width: ${THUMB}px;
              height: ${THUMB}px;
              border-radius: 50%;
              background: white;
              border: 2px solid ${trackColor};
              box-shadow: 0 1px 3px rgba(0,0,0,0.12);
              cursor: pointer;
              transition: box-shadow 0.15s;
            }
            input[type="range"].${cls}::-webkit-slider-thumb:hover {
              box-shadow: 0 0 0 4px rgba(var(--color-primary-500),0.15);
            }
            input[type="range"].${cls}::-moz-range-thumb {
              pointer-events: all;
              width: ${THUMB}px;
              height: ${THUMB}px;
              border-radius: 50%;
              background: white;
              border: 2px solid ${trackColor};
              box-shadow: 0 1px 3px rgba(0,0,0,0.12);
              cursor: pointer;
            }
            input[type="range"].${cls}:disabled::-webkit-slider-thumb {
              cursor: default;
              pointer-events: none;
            }
            input[type="range"].${cls}:disabled::-moz-range-thumb {
              cursor: default;
              pointer-events: none;
            }
          `,
        }}
      />
    </Box>
  );
};

export default RangeSlider;
